import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Clock, FileText, Receipt, AlertCircle } from "lucide-react";

const STAGE_BADGE = {
    request_received: { color: "var(--color-text-muted)", bg: "var(--color-bg-card-2)" },
    technician_review: { color: "var(--color-accent)", bg: "rgba(40, 216, 224, 0.12)" },
    estimate_being_prepared: { color: "var(--color-accent)", bg: "rgba(40, 216, 224, 0.12)" },
    estimate_sent: { color: "var(--color-accent)", bg: "rgba(40, 216, 224, 0.12)" },
    awaiting_approval: { color: "var(--color-warning)", bg: "rgba(202, 138, 4, 0.12)" },
    approved: { color: "var(--color-success)", bg: "rgba(52, 211, 153, 0.12)" },
    invoice_sent: { color: "var(--color-warning)", bg: "rgba(202, 138, 4, 0.12)" },
    paid: { color: "var(--color-success)", bg: "rgba(52, 211, 153, 0.12)" },
    scheduled: { color: "var(--color-accent)", bg: "rgba(40, 216, 224, 0.12)" },
    completed: { color: "var(--color-success)", bg: "rgba(52, 211, 153, 0.12)" },
    canceled: { color: "var(--color-danger)", bg: "rgba(248, 113, 113, 0.12)" },
};

export default function CustomerDashboard() {
    const { me } = useOutletContext();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/customer/leads")
            .then((r) => { setLeads(r.data.leads || []); })
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-6" data-testid="customer-dashboard">
            {me?.is_test_seed ? (
                <div
                    className="a1-card flex items-start gap-3"
                    style={{
                        background: "rgba(255, 214, 95, 0.08)",
                        borderColor: "var(--color-warning)",
                    }}
                    data-testid="customer-preview-banner"
                >
                    <AlertCircle size={20} className="text-[var(--color-warning)] shrink-0 mt-0.5" />
                    <div>
                        <p className="font-bold m-0 mb-1" style={{ color: "var(--color-warning)" }}>
                            Tester Week customer preview
                        </p>
                        <p className="text-sm m-0 leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                            This is the view a real customer would see. The data shown here is a dry-run sample seeded for John's tester week — no real money, email, or text has been sent. Admin-only notes are never shown on this screen.
                        </p>
                    </div>
                </div>
            ) : null}
            <div>
                <p className="a1-eyebrow mb-2">Welcome</p>
                <h1 className="text-3xl mb-2">Hi, {me?.name || "there"}</h1>
                <p style={{ color: "var(--color-text-muted)" }}>
                    Here are your A1 Screening requests, estimates, invoices, and payments — all in one place.
                </p>
            </div>

            <div className="a1-card a1-card-feature">
                <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                    <strong className="a1-accent">Disclaimer:</strong> Final pricing is confirmed by A1 Screening after technician review.
                </p>
            </div>

            <ContactCardLite me={me} />

            <section data-testid="customer-requests">
                <h2 className="text-xl mb-4 a1-h2-underline">Your requests</h2>
                {loading ? (
                    <p style={{ color: "var(--color-text-muted)" }}>Loading…</p>
                ) : leads.length === 0 ? (
                    <p style={{ color: "var(--color-text-muted)" }}>
                        No requests yet. When you submit a free estimate or upload photos, they'll appear here.
                    </p>
                ) : (
                    <div className="grid gap-4">
                        {leads.map((lead) => <LeadRow key={lead.id} lead={lead} />)}
                    </div>
                )}
            </section>
        </div>
    );
}

function ContactCardLite({ me }) {
    if (!me) return null;
    return (
        <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Phone" value={me.phone} />
            <Field label="Email" value={me.email} />
            <Field label="Service address" value={me.service_address || me.city} />
            <Field label="City" value={me.city} />
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div className="a1-card">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-1" style={{ color: "var(--color-text-dim)" }}>{label}</p>
            <p className="text-base font-semibold" style={{ color: "var(--color-text)" }}>{value || "—"}</p>
        </div>
    );
}

function LeadRow({ lead }) {
    const stageStyle = STAGE_BADGE[lead.workflow_stage] || STAGE_BADGE.request_received;
    const inv = lead.invoice || null;
    const pay = lead.payment || null;
    const est = lead.estimate || null;
    const paid = pay?.amount_paid || 0;
    const balance = pay?.balance_due ?? null;
    const partial = paid > 0 && balance > 0;
    const failed = pay?.status === "failed";
    const canceled = lead.workflow_stage === "canceled";

    return (
        <div className="a1-card flex flex-col gap-3" data-testid={`customer-lead-${lead.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-base font-semibold" style={{ color: "var(--color-text)" }}>
                        {lead.service_needed || "Service request"}
                    </p>
                    <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                        Submitted {new Date(lead.created_at).toLocaleDateString()}
                    </p>
                </div>
                <span
                    className="a1-badge uppercase tracking-wider"
                    style={{ background: stageStyle.bg, color: stageStyle.color }}
                    data-testid={`customer-lead-stage-${lead.id}`}
                >
                    {lead.workflow_label}
                </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <Detail icon={Clock} label="Technician review" value={lead.technician_review_status} />
                {lead.panel_count ? <Detail icon={FileText} label="Panel count" value={lead.panel_count} /> : null}
                {lead.access_difficulty ? <Detail icon={AlertCircle} label="Access" value={lead.access_difficulty} /> : null}
                {est?.amount_quoted ? (
                    <Detail icon={FileText} label="Estimate" value={`$${est.amount_quoted}`} />
                ) : (
                    <Detail icon={FileText} label="Estimate" value="Not yet prepared" />
                )}
                {inv?.status ? (
                    <Detail icon={Receipt} label="Invoice" value={inv.status} />
                ) : null}
                {pay ? (
                    <Detail
                        icon={failed ? AlertCircle : CheckCircle2}
                        label={failed ? "Payment status" : "Payment"}
                        value={
                            failed
                                ? "Last attempt failed — please contact A1 Screening"
                                : paid > 0
                                ? `Paid $${paid}${balance ? ` · Balance $${balance}` : ""}`
                                : "Awaiting payment"
                        }
                    />
                ) : null}
                {canceled ? (
                    <Detail
                        icon={AlertCircle}
                        label="Status"
                        value="Job canceled — please contact A1 Screening if this is a mistake."
                    />
                ) : null}
            </div>
            {partial ? (
                <div
                    className="text-xs px-3 py-2 rounded-md"
                    style={{ background: "rgba(202, 138, 4, 0.12)", color: "var(--color-warning)" }}
                    data-testid={`customer-lead-partial-${lead.id}`}
                >
                    Partial payment received. Outstanding balance ${balance}.
                </div>
            ) : null}
            <p className="a1-helper">{lead.disclaimer}</p>
        </div>
    );
}

function Detail({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-2">
            <Icon size={16} className="text-[var(--color-accent)] mt-0.5 shrink-0" />
            <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--color-text-dim)" }}>{label}</p>
                <p className="text-sm font-semibold" style={{ color: "var(--color-text)" }}>{value}</p>
            </div>
        </div>
    );
}
